<template>
  <div class="contract-change-details-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('contractChangeDetails.page.title')" :subtitle="$t('contractChangeDetails.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">{{ $t('common.back') }}</el-button>
      </template>
    </ds-page-header>

    <!-- (2) 变更前后快照 -->
    <div v-for="(item, index) in contractChangeDetailsInfo.contractDetails" :key="index" class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">
          {{ $t('contractChangeDetails.contract') }}
          {{ item.operate === 'ADD' ? $t('contractChangeDetails.afterChange') : $t('contractChangeDetails.beforeChange') }}
          {{ item.operate === 'ADD' ? '(' + item.planTypeName + ')' : '' }}
        </span>
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.contractName') }}</span>
          <span class="ds-info-grid__value">{{ item.contractName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.contractCode') }}</span>
          <span class="ds-info-grid__value ds-num">{{ item.contractCode || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.contractType') }}</span>
          <span class="ds-info-grid__value">{{ item.contractType || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.partyA') }}</span>
          <span class="ds-info-grid__value">{{ item.partyA || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.partyAContacts') }}</span>
          <span class="ds-info-grid__value">{{ item.aContacts || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.partyALink') }}</span>
          <span class="ds-info-grid__value ds-num">{{ item.aLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.partyB') }}</span>
          <span class="ds-info-grid__value">{{ item.partyB || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.partyBContacts') }}</span>
          <span class="ds-info-grid__value">{{ item.bContacts || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.partyBLink') }}</span>
          <span class="ds-info-grid__value ds-num">{{ item.bLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.operator') }}</span>
          <span class="ds-info-grid__value">{{ item.operator || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.operatorLink') }}</span>
          <span class="ds-info-grid__value ds-num">{{ item.operatorLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.amount') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatMoney(item.amount) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.startTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ item.startTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.endTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ item.endTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractChangeDetails.signingTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ item.signingTime || '-' }}</span>
        </div>
      </div>
    </div>

    <!-- (3) 审批流 -->
    <div class="ds-panel">
      <div class="ds-panel__header ds-panel__header--padded">
        <span class="ds-panel__title">{{ $t('contractChangeDetails.workflow') }}</span>
      </div>
      <ds-data-table :data="contractChangeDetailsInfo.auditUsers" border
        :empty-description="$t('contractChangeDetails.workflow')">
        <el-table-column :label="$t('contractChangeDetails.serialNumber')" width="80" align="center">
          <template slot-scope="scope"><span class="ds-num">{{ scope.$index + 1 }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('contractChangeDetails.handler')" align="center" min-width="120">
          <template slot-scope="scope">{{ scope.row.userName || scope.row.auditName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('contractChangeDetails.status')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName" type="info" />
          </template>
        </el-table-column>
        <el-table-column prop="auditTime" :label="$t('contractChangeDetails.processTime')" align="center" width="160">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.auditTime || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="duration" :label="$t('contractChangeDetails.timeConsuming')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.duration || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="message" :label="$t('contractChangeDetails.opinion')" align="center" min-width="160" show-overflow-tooltip />
      </ds-data-table>
    </div>
  </div>
</template>

<script>
import { getContractChangeDetails, getAuditUsers } from '@/api/resource/contractChangeDetailsApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ContractChangeDetailsList',
  components: { DsPageHeader, DsDataTable, DsStatusTag },
  data() {
    return {
      contractChangeDetailsInfo: { contractDetails: [], auditUsers: [], planId: '' },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.contractChangeDetailsInfo.planId = this.$route.query.planId
    this._listContractDetails()
    this._loadAuditUser()
  },
  methods: {
    formatMoney(v) { return formatMoney(v) },
    async _listContractDetails() {
      try {
        const { data } = await getContractChangeDetails({ page: 1, row: 10, planId: this.contractChangeDetailsInfo.planId })
        const list = Array.isArray(data) ? data.slice() : []
        list.sort((_child, _newChild) => _newChild.operate.charCodeAt(0) - _child.operate.charCodeAt(0))
        this.contractChangeDetailsInfo.contractDetails = list
      } catch (error) { console.error('请求合同详情失败:', error) }
    },
    async _loadAuditUser() {
      try {
        const { data } = await getAuditUsers({ businessKey: this.contractChangeDetailsInfo.planId, communityId: this.communityId })
        this.contractChangeDetailsInfo.auditUsers = Array.isArray(data) ? data : []
      } catch (error) { console.error('请求审核用户失败:', error) }
    },
    _goBack() { this.$router.go(-1) }
  }
}
</script>

<style lang="scss" scoped>
.contract-change-details-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
}
.ds-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--ds-spacing-md);
  margin-bottom: var(--ds-spacing-lg);
  border-bottom: 1px solid var(--ds-gray-200);
  &--padded { margin: 0; padding: var(--ds-spacing-md) var(--ds-spacing-lg); }
}
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  &__item { display: flex; flex-direction: column; gap: 4px; }
  &__label { font-size: var(--ds-font-size-sm); color: var(--ds-gray-500); }
  &__value { font-size: var(--ds-font-size-md); color: var(--ds-gray-800); word-break: break-all; }
}
</style>
